import {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';

import {useSelector, shallowEqual, useDispatch} from 'react-redux';

import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {CL_RX_METHOD, PRESCRIPTION_STATUS} from 'constants/success';
import {ApplicationStoreType} from 'desktop/store';
import {useHostDispatch, useHostSelector} from 'hooks/hostHooks/hostHooks';
import {HostApplicationStoreType} from 'hostApplicationStoreType';
import {getIsAllItemsOfOrderUpdated} from 'libs/success/helpers/helpers';
import {getIsPopupOpen} from 'selectors/authPopup';
import {getIsCustomerLogged} from 'selectors/customer';

import {SEND_LATER, UPLOAD, FILL_IT} from 'types/successPage';

import {getDrawerState, getOrderDetails} from '../../selectorsV2';

import {getPrescriptionStatus} from '../../selectorsV2/prescription';
import {setDrawerMethod, setDrawerState} from '../../store/reducers/drawerPopup/drawerPopup';
import {setIsOrderStatusHasError} from '../../store/reducers/orderStatus/orderStatus';
import {getOrderError, getOrderItems} from '../../store/selectors/orderStatus/orderStatus';

/**
 * Hook to listen if the user logged in
 */
export const useListeners = () => {
    const {isError, items, orderItems, originalStatus, rxMethod} = useSelector(
        (store: ApplicationStoreType) => ({
            isError: getOrderError(store),
            items: getOrderItems(store),
            orderItems: getOrderDetails(store).items,
            originalStatus: getPrescriptionStatus(store),
            rxMethod: getDrawerState(store).rxMethod,
        }),
        shallowEqual,
    );

    const dispatch = useDispatch();
    const dispatchToHost = useHostDispatch();
    const isVerification =
        getIsAllItemsOfOrderUpdated(items) || originalStatus === PRESCRIPTION_STATUS.UPLOAD;
    const {isPopupOpen, isUserLoggedIn} = useHostSelector(
        (store: HostApplicationStoreType) => ({
            isPopupOpen: getIsPopupOpen(store),
            isUserLoggedIn: getIsCustomerLogged(store),
        }),
        shallowEqual,
    );
    useEffect(() => {
        if (isPopupOpen || rxMethod !== CL_RX_METHOD.LOGIN || isUserLoggedIn) return;
        sendDesktopAnalyticsEvent(TYP_CONTACT_LENSES, 'Online Exam - Login Popup closed');

        dispatch(
            setDrawerMethod({
                rxMethod: CL_RX_METHOD.EYE_EXAM,
            }),
        );
        dispatch(setDrawerState(true));
    }, [isPopupOpen, isUserLoggedIn, rxMethod]);

    useEffect(() => {
        if (!isUserLoggedIn || rxMethod !== CL_RX_METHOD.LOGIN) return;
        dispatch(
            setDrawerMethod({
                isOpen: true,
                rxMethod: CL_RX_METHOD.EYE_EXAM_ELIGIBILITY,
            }),
        );
        dispatchToHost({
            payload: {
                tab: 'login',
                visible: false,
            },
            type: 'AUTH_POPUP@SET_VISIBLE',
        });
    }, [isPopupOpen, isUserLoggedIn, rxMethod]);

    useEffect(() => {
        const productTypes: string[] = [];

        orderItems?.forEach(item => {
            if (item.prescription?.send_by === SEND_LATER)
                productTypes.push('No Prescription Uploaded');
            if (getIsAllItemsOfOrderUpdated(items) || item.prescription?.send_by === UPLOAD)
                productTypes.push('Prescription uploaded successfully');
            if (item.prescription?.send_by === FILL_IT) {
                productTypes.push('Confirm Prescription');
            }
        });
        if (productTypes.length)
            sendDesktopAnalyticsEvent(
                TYP_CONTACT_LENSES,
                `Prescription Type Products - ${productTypes.join(', ')}`,
                false,
            );
    }, [getIsAllItemsOfOrderUpdated, orderItems]);

    useEffect(() => {
        dispatchToHost({payload: {isHidden: !isVerification}, type: 'CHANGE_FOOTER_VIEW'});
    }, [isVerification]);

    useEffect(() => {
        if (isError) dispatch(setIsOrderStatusHasError(false));
    }, [rxMethod]);
};
